package com.fantasytenis.shared.domain.skills.entity

import com.fantasytenis.shared.domain.lineup.entity.Category
import kotlinx.serialization.Serializable

/**
 * Trend direction for week-over-week skill changes
 */
enum class TrendDirection {
    UP,
    DOWN,
    STABLE
}

/**
 * Skill trend representing week-over-week change
 */
data class SkillTrend(
    val direction: TrendDirection,
    val delta: Int
) {
    companion object {
        fun fromDelta(currentCost: Int, previousCost: Int?): SkillTrend {
            return if (previousCost == null) {
                SkillTrend(TrendDirection.STABLE, 0)
            } else {
                val delta = currentCost - previousCost
                val direction = when {
                    delta > 0 -> TrendDirection.UP
                    delta < 0 -> TrendDirection.DOWN
                    else -> TrendDirection.STABLE
                }
                SkillTrend(direction, kotlin.math.abs(delta))
            }
        }
    }
}

/**
 * Skill tier representing player skill level
 */
enum class SkillTier(val label: String, val minCost: Int, val maxCost: Int) {
    ELITE("Elite", 11, 12),
    STRONG("Strong", 8, 10),
    SOLID("Solid", 5, 7),
    DEVELOPING("Developing", 1, 4);

    companion object {
        fun fromCost(cost: Int): SkillTier {
            return entries.first { cost in it.minCost..it.maxCost }
        }
    }
}

/**
 * Gender filter for leaderboards
 */
enum class Gender(val code: String, val displayName: String) {
    MALE("M", "ATP"),
    FEMALE("F", "WTA");

    companion object {
        fun fromCode(code: String): Gender? {
            return entries.find { it.code == code }
        }
    }
}

/**
 * Skill leaderboard entry entity
 */
data class SkillLeaderboardEntry(
    val playerId: String,
    val playerName: String,
    val country: String,
    val atpRanking: Int?,
    val skillCost: Int,
    val skillRank: Int,
    val trend: SkillTrend,
    val gender: Gender
) {
    val tier: SkillTier
        get() = SkillTier.fromCost(skillCost)
}

/**
 * Player costs for skill breakdown
 */
@Serializable
data class PlayerSkillCosts(
    val serve: Int,
    val forehand: Int,
    val backhand: Int,
    val volleys: Int,
    val fighting: Int
) {
    val total: Int
        get() = serve + forehand + backhand + volleys + fighting

    val average: Double
        get() = total / 5.0

    fun getCostForCategory(category: Category): Int {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }
}

/**
 * Weekly skill snapshot for history tracking
 */
data class WeeklySkillSnapshot(
    val weekOf: String,
    val costs: PlayerSkillCosts
)

/**
 * Player skill history entity
 */
data class PlayerSkillHistory(
    val playerId: String,
    val playerName: String,
    val currentCosts: PlayerSkillCosts,
    val history: List<WeeklySkillSnapshot>
) {
    /**
     * Get trend for a specific category
     */
    fun getTrendForCategory(category: Category): SkillTrend {
        val currentCost = currentCosts.getCostForCategory(category)
        val previousCost = history.firstOrNull()?.costs?.getCostForCategory(category)
        return SkillTrend.fromDelta(currentCost, previousCost)
    }

    /**
     * Get costs for a specific week
     */
    fun getCostsForWeek(weekOf: String): PlayerSkillCosts? {
        return history.find { it.weekOf == weekOf }?.costs
    }
}

/**
 * Trending player entity
 */
data class TrendingPlayer(
    val playerId: String,
    val playerName: String,
    val country: String,
    val atpRanking: Int?,
    val category: Category,
    val currentCost: Int,
    val previousCost: Int,
    val delta: Int
) {
    val trend: SkillTrend
        get() = SkillTrend.fromDelta(currentCost, previousCost)
}

/**
 * Top skill info for hidden gems
 */
data class TopSkillInfo(
    val category: Category,
    val cost: Int
)

/**
 * Hidden gem player entity
 */
data class HiddenGemPlayer(
    val playerId: String,
    val playerName: String,
    val country: String,
    val atpRanking: Int?,
    val averageCost: Double,
    val topSkill: TopSkillInfo,
    val costs: PlayerSkillCosts
) {
    /**
     * Value score: higher average cost with lower ranking = better value
     */
    val valueScore: Double
        get() = if (atpRanking != null && atpRanking > 0) {
            averageCost / kotlin.math.sqrt(atpRanking.toDouble())
        } else {
            averageCost
        }
}
